import { useCallback, useState } from 'react'

/**
 * 퍼널(단계별 화면 전환) 관리 훅
 *
 * 브라우저 히스토리를 사용하지 않음 (WebView 환경에서 history API 사용 금지)
 * 뒤로가기 없이 앞으로만 진행하는 퍼널 패턴에 최적화
 *
 * ## 사용 예시
 * ```tsx
 * const { step, data, setStep } = useFunnel<
 *   'coupon' | 'drawing' | 'result',
 *   { couponId?: string; grade?: string }
 * >('coupon')
 *
 * return (
 *   <>
 *     {step === 'coupon' && <CouponStep onNext={() => setStep('drawing')} />}
 *     {step === 'drawing' && (
 *       <DrawingStep onComplete={(result) => setStep('result', { couponId: result.id, grade: result.grade })} />
 *     )}
 *     {step === 'result' && <ResultStep couponId={data.couponId} grade={data.grade} />}
 *   </>
 * )
 * ```
 */
export function useFunnel<TStep extends string, TData extends object = Record<string, never>>(
  initialStep: TStep,
  initialData?: TData,
) {
  const [step, setStepState] = useState<TStep>(initialStep)
  const [data, setData] = useState<Partial<TData>>(initialData ?? {})

  /** step 전환. 데이터를 넘기면 기존 데이터에 머지됨 */
  const setStep = useCallback((nextStep: TStep, nextData?: Partial<TData>) => {
    setStepState(nextStep)
    if (nextData) {
      setData((prev) => ({ ...prev, ...nextData }))
    }
  }, [])

  /** 초기 상태로 리셋 */
  const reset = useCallback(() => {
    setStepState(initialStep)
    setData(initialData ?? {})
  }, [initialStep, initialData])

  return { step, data, setStep, reset }
}
